import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';

export type ThemeMode = 'light' | 'dark';
export type ThemeStyle = 'operations' | 'standard';

const MODE_KEY = 'ut-theme-mode';
const STYLE_KEY = 'ut-theme-style';

const DEFAULT_MODE: ThemeMode = 'light';
const DEFAULT_STYLE: ThemeStyle = 'standard';

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
    if (typeof window === 'undefined') return fallback;
    const stored = window.localStorage.getItem(key);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
}

function writeStored(key: string, value: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, value);
}

/**
 * Singleton service that manages theme state (mode and style), persisted to
 * localStorage so the choice survives reloads. Extends BaseViewModel so React
 * adapters can subscribe to changes.
 */
@injectable()
export class ThemeService extends BaseViewModel {
    mode: ThemeMode = readStored(MODE_KEY, ['light', 'dark'], DEFAULT_MODE);
    style: ThemeStyle = readStored(STYLE_KEY, ['operations', 'standard'], DEFAULT_STYLE);

    setMode(mode: ThemeMode): void {
        this.mode = mode;
        writeStored(MODE_KEY, mode);
        this.notifyChange();
    }

    setStyle(style: ThemeStyle): void {
        this.style = style;
        writeStored(STYLE_KEY, style);
        this.notifyChange();
    }
}
